<script setup>
// Tools (phase 4.7): the kit's plant — Invitation, the kit's table (R2-4-7 = A) with the kit's pagination, the
// soft-limit notice as the kit's Alert, the empty state over the faded table (D-G5), and the editor as a drawer
// (CustomToolEditorDrawer, D-G6 + Q1) instead of the dialog. Same store calls; visible only with polaris_custom_tools
// (route meta), as before.
import { computed, onMounted, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { WarningAlt16 } from '@carbon/icons-vue';
import {
  Alert,
  AlertDescription,
} from 'dashboard/components-next/polaris/ui/alert';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import CustomToolsPageEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/CustomToolsPageEmptyState.vue';
import CustomToolEditorDrawer from 'dashboard/components-next/polaris/pageComponents/customTool/CustomToolEditorDrawer.vue';
import CustomToolsTable from 'dashboard/components-next/polaris/pageComponents/customTool/CustomToolsTable.vue';
import DataSourcesSection from 'dashboard/components-next/polaris/pageComponents/dataSource/DataSourcesSection.vue';
import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';

const store = useStore();
const { isFeatureFlagEnabled, shouldShowPaywall } = usePolicy();

const SOFT_LIMIT = 10;
const isV2 = computed(() => isFeatureFlagEnabled(FEATURE_FLAGS.POLARIS_V2));

const uiFlags = useMapGetter('polarisCustomTools/getUIFlags');
const customTools = useMapGetter('polarisCustomTools/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const customToolsMeta = useMapGetter('polarisCustomTools/getMeta');

const showSoftLimitWarning = computed(
  () => !isV2.value && customToolsMeta.value.totalCount > SOFT_LIMIT
);

const deleteDialogRef = ref(null);
const selectedTool = ref(null);
const editorOpen = ref(false);
const editingTool = ref(null);
const editorDrawer = ref(null);

const fetchCustomTools = (page = 1) => {
  store.dispatch('polarisCustomTools/get', { page });
};

const onPageChange = page => fetchCustomTools(page);

const openEditor = tool => {
  const show = () => {
    editingTool.value = tool;
    editorOpen.value = true;
  };
  // Switching rows with the drawer open asks first when there are unsaved changes.
  if (editorOpen.value && editorDrawer.value) {
    editorDrawer.value.runGuarded(show);
  } else show();
};

const handleDelete = tool => {
  selectedTool.value = tool;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  const tool = customTools.value.find(item => item.id === id);
  if (action === 'edit') {
    openEditor(tool);
  } else if (action === 'delete') {
    handleDelete(tool);
  }
};

const onDeleteSuccess = () => {
  if (editingTool.value?.id === selectedTool.value?.id) {
    editorOpen.value = false;
  }
  selectedTool.value = null;
  // Check if page will be empty after deletion
  if (customTools.value.length === 1 && customToolsMeta.value.page > 1) {
    // Go to previous page if current page will be empty
    onPageChange(customToolsMeta.value.page - 1);
  } else {
    // Refresh current page
    fetchCustomTools(customToolsMeta.value.page);
  }
};

// Passo "Integrações" do setup (spec §4.2): conta como feito ao ser visitado. O PATCH de update do assistente
// substitui o config inteiro, então o registro é recarregado SEMPRE antes do PATCH — um config velho da store
// apagaria marcas gravadas por outro caminho (ex.: o Playground gravando setup_test_done no servidor).
const route = useRoute();
const markIntegrationsVisited = async () => {
  const assistantId = Number(route.params.assistantId);
  if (!assistantId) return;
  try {
    await store.dispatch('polarisAssistants/show', assistantId);
    const assistant = store.getters['polarisAssistants/getRecord'](assistantId);
    const config = assistant?.config || {};
    if (config.setup_integrations_visited) return;
    await store.dispatch('polarisAssistants/update', {
      id: assistantId,
      config: { ...config, setup_integrations_visited: true },
    });
    store.dispatch('polarisSetupStatus/fetch', assistantId, { force: true });
  } catch {
    // Agentes não podem atualizar o assistente (a política exige administrador): a marca fica para a visita de um admin.
  }
};

onMounted(() => {
  if (!shouldShowPaywall(FEATURE_FLAGS.POLARIS_CUSTOM_TOOLS)) {
    fetchCustomTools();
  }
  markIntegrationsVisited();
});
</script>

<template>
  <PageLayout
    preview="open"
    :header-title="$t('POLARIS.CUSTOM_TOOLS.HEADER')"
    :button-label="$t('POLARIS.CUSTOM_TOOLS.ADD_NEW')"
    :button-policy="['administrator']"
    :feature-flag="FEATURE_FLAGS.POLARIS_CUSTOM_TOOLS"
    :total-count="customToolsMeta.totalCount"
    :current-page="customToolsMeta.page"
    :show-pagination-footer="!isFetching && !!customTools.length"
    :is-fetching="isFetching"
    :is-empty="!customTools.length"
    :show-know-more="false"
    :invitation="{
      title: $t('POLARIS.CUSTOM_TOOLS.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE'),
      text: $t('POLARIS.CUSTOM_TOOLS.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE'),
      actionLabel: $t('POLARIS.CUSTOM_TOOLS.ADD_NEW'),
    }"
    invitation-key="tools"
    @update:current-page="onPageChange"
    @click="openEditor(null)"
  >
    <template #paywall />

    <template #emptyState>
      <CustomToolsPageEmptyState @click="openEditor(null)" />
      <div class="my-4 border-t border-n-weak" />
      <DataSourcesSection />
    </template>

    <template #body>
      <Alert v-if="showSoftLimitWarning" role="status">
        <WarningAlt16 aria-hidden="true" />
        <AlertDescription class="text-polaris-foreground">
          {{ $t('POLARIS.CUSTOM_TOOLS.SOFT_LIMIT_WARNING') }}
        </AlertDescription>
      </Alert>
      <CustomToolsTable :tools="customTools" @action="handleAction" />
      <div class="my-4 border-t border-n-weak" />
      <DataSourcesSection />
    </template>

    <DeleteDialog
      v-if="selectedTool"
      ref="deleteDialogRef"
      :entity="selectedTool"
      type="CustomTools"
      translation-key="CUSTOM_TOOLS"
      @delete-success="onDeleteSuccess"
    />

    <template #aside>
      <CustomToolEditorDrawer
        ref="editorDrawer"
        v-model:open="editorOpen"
        :tool="editingTool"
      />
    </template>
  </PageLayout>
</template>
